import React from 'react';
import { CHOICES_CALL_KPI } from '@ctd/agent-contract';
import { isTurnActive } from '../utils/agent-thread-state';
import type { AgentCall, AgentTurn } from '../utils/agent-thread-state';
import { ActivityLine } from './activity-line';
import { AgentCard } from './agent-card';
import { ChoicesCard } from './choices-card';
import { FailedCard } from './failed-card';
import style from '../style.module.scss';

const FATAL_ERROR =
    'Something went wrong. Your conversation is saved, try again.';

const isChoices = (call: AgentCall) => call.kpi === CHOICES_CALL_KPI;

const TurnContent: React.FC<{
    turn: AgentTurn;
    answeredWith?: string;
    onAnswer?: (text: string) => void;
}> = ({ turn, answeredWith, onAnswer }) => {
    const turnActive = isTurnActive(turn.phase);
    const calls = turn.calls.filter((call) => !isChoices(call));
    const choiceCards = turn.calls.flatMap((call) =>
        isChoices(call) && call.card?.shape === 'choices'
            ? [{ id: call.id, card: call.card }]
            : []
    );
    const slots = calls.filter((call) => call.card || call.status === 'failed');
    const isThinking =
        turn.phase === 'working' &&
        !calls.some((call) => call.status === 'running');

    return (
        <div className={style.turn}>
            <p className={style.question}>{turn.question}</p>
            {calls.length > 0 && (
                <ul className={style.calls}>
                    {calls.map((call) => (
                        <ActivityLine
                            key={call.id}
                            call={call}
                            running={turnActive && call.status === 'running'}
                        />
                    ))}
                </ul>
            )}
            {isThinking && (
                <p className={style.activity} role="status">
                    <span className={style.pulse} aria-hidden="true" />
                    Working
                </p>
            )}
            {slots.length > 0 && (
                <div className={style.results}>
                    {slots.map((call) =>
                        call.card ? (
                            <AgentCard key={call.id} card={call.card} />
                        ) : (
                            <FailedCard key={call.id} call={call} />
                        )
                    )}
                </div>
            )}
            {turn.answer && <p className={style.answer}>{turn.answer}</p>}
            {choiceCards.map(({ id, card }) => (
                <ChoicesCard
                    key={id}
                    card={card}
                    answeredWith={answeredWith}
                    onAnswer={turn.phase === 'done' ? onAnswer : undefined}
                />
            ))}
            {turn.phase === 'stopped' && (
                <p className={style.stopped}>Stopped</p>
            )}
            {turn.phase === 'error' && (
                <p className={style.error} role="alert">
                    {FATAL_ERROR}
                </p>
            )}
            {turn.phase === 'done' &&
                !turn.answer &&
                slots.length === 0 &&
                choiceCards.length === 0 && (
                    <p className={style.empty}>No results for that question.</p>
                )}
        </div>
    );
};

/** One question and the agent's answer: activity lines, cards, text, choices, then how it ended. */
export const TurnView = React.memo(TurnContent);
